import type { GraphNodeStep } from '@/components/UI/GraphCanvas/nodes/Nodes.utils';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionStatus, AgentTreeNodeApiId } from '@/model/Agent.model';
import type { AgentGraphNode } from './AgentTree.utils';

/**
 * The SEND validator's session graph as a chain of steps: detect the domains, confirm them, write the
 * report, view it. The server's tree holds the detect root, the confirm node and the result node; the
 * "Writing report" step between confirmation and the result is the UI's, derived here from the
 * session's status, so the server tree is unchanged.
 */

const DETECTED_TITLE = 'Detected domains';
const UNREADABLE_TITLE = 'SEND files could not be read';
const WRITING_REPORT_TITLE = 'Writing report';
const NOTHING_CHECKED_LABEL = 'Nothing was checked';

/**
 * A step that tracks the run itself. `awaiting_input` maps to pending, though no tracked step is ever
 * shown while the run is parked.
 */
export const stepOfStatus = (status: AgentSessionStatus): GraphNodeStep => {
  switch (status) {
    case 'running':
      return { state: 'running', label: 'Running' };
    case 'awaiting_input':
      return { state: 'pending' };
    case 'completed':
      return { state: 'done', label: 'Completed' };
    case 'failed':
      return { state: 'failed', label: 'Failed' };
    case 'cancelled':
      return { state: 'failed', label: 'Cancelled' };
  }
};

/**
 * Whether the user has confirmed the detected domains. The server reports `awaiting_input` only while
 * the detect run is parked on the confirm node, and un-parks it only in the transaction that queues the
 * validate run on confirmation, so a confirm node under any other status has been confirmed. Cancelling a
 * session leaves a parked run parked (cancellation touches only queued and running runs), so a session
 * cancelled at the confirm step still reads `awaiting_input`.
 *
 * Two cases the tree alone cannot tell apart. A detect run that fails after writing its confirm node
 * reads as confirmed and then failed. A page opened in the moment between the node being written and
 * the run parking reads as confirmed until the detect run's `completed` event reconciles the tree.
 */
const isConfirmed = (status: AgentSessionStatus): boolean => status !== 'awaiting_input';

/** The "Writing report" step's id: derived from the confirm node's, so every pass and the stream agree on it. */
export const writingReportStepId = (confirmApiId: AgentTreeNodeApiId): AgentTreeNodeApiId =>
  `${confirmApiId}-writing-report`;

/** The UI-only "Writing report" step, hanging off the confirm node. */
export const writingReportStep = (confirm: AgentGraphNode, step: GraphNodeStep): AgentGraphNode => {
  const apiId = writingReportStepId(confirm.data.apiId);
  return {
    id: apiId,
    type: 'send_writing_report',
    position: { x: 0, y: 0 },
    data: {
      apiId,
      parentApiId: confirm.data.apiId,
      type: 'result',
      sequenceNumber: confirm.data.sequenceNumber,
      variant: 'send_writing_report',
      title: WRITING_REPORT_TITLE,
      step,
    },
  };
};

/** Hangs a validation's result off the "Writing report" step rather than the confirm node it was written under. */
export const underWritingReport = (result: AgentGraphNode, confirm: AgentGraphNode): AgentGraphNode => ({
  ...result,
  data: { ...result.data, parentApiId: writingReportStepId(confirm.data.apiId) },
});

/**
 * Where detection got to: still running (or ended without a result), done with detections waiting on the
 * confirm node, or stopped at the input gate, which writes its result straight under the root.
 */
export type SendRootStage = 'detecting' | 'detected' | 'unreadable';

/**
 * The SEND root as a step. A detection the input gate stopped checked nothing, so it reads as the report
 * does — the files could not be read — and never as a completed step.
 */
const rootStepData = (stage: SendRootStage, status: AgentSessionStatus) => {
  switch (stage) {
    case 'detected':
      return { title: DETECTED_TITLE, step: { state: 'done' as const, label: 'Completed' } };
    case 'unreadable':
      return { title: UNREADABLE_TITLE, step: { state: 'failed' as const, label: NOTHING_CHECKED_LABEL } };
    case 'detecting':
      return { step: stepOfStatus(status) };
  }
};

/** The SEND root as a step: see {@link SendRootStage}. */
export const sendRootStep = (root: AgentGraphNode, stage: SendRootStage, status: AgentSessionStatus): AgentGraphNode =>
  root.data.variant === 'read_only'
    ? { ...root, data: { ...root.data, badge: undefined, ...rootStepData(stage, status) } }
    : root;

/** Whether a validation's result, hung off the confirm node, is the one this node is. */
const isConfirmedRunResult = (node: AgentGraphNode, confirm: AgentGraphNode): boolean =>
  node.data.variant === 'send_result' && node.data.parentApiId === confirm.id;

/**
 * The "Writing report" step for a confirmed run. A run its input stopped wrote a result saying nothing
 * was checked (the server's `outcome`), so the step fails rather than completes, however the session
 * ended. A result node stored before outcomes existed says nothing, and the step follows the run.
 */
const writingReportStepOf = (
  nodes: ReadonlyArray<AgentGraphNode>,
  confirm: AgentGraphNode,
  status: AgentSessionStatus
): GraphNodeStep => {
  const nothingChecked = nodes.some(
    (node) =>
      isConfirmedRunResult(node, confirm) &&
      node.data.variant === 'send_result' &&
      node.data.outcome === 'nothing_checked'
  );
  return nothingChecked ? { state: 'failed', label: NOTHING_CHECKED_LABEL } : stepOfStatus(status);
};

/**
 * Derives the SEND step chain over a whole mapped tree. The root shows detection's state, the confirm
 * node is pending until confirmed and done after (still clickable, so the modal can be reopened to see
 * what was confirmed), and once confirmed a "Writing report" step carries the validation run's state,
 * with the result re-parented under it — or a failed "Nothing was checked" when the input gate stopped
 * the run after confirmation.
 *
 * A result written straight under the root (a detection stopped on unreadable input) has no confirm
 * node, so nothing is added, and the root reads as the report does: nothing was checked.
 */
export const withSendSteps = (
  nodes: ReadonlyArray<AgentGraphNode>,
  status: AgentSessionStatus
): Array<AgentGraphNode> => {
  const confirm = nodes.find((node) => node.data.variant === 'send_domain_confirm');
  if (_isNil(confirm)) {
    const root = nodes.find((node) => _isNil(node.data.parentApiId));
    const unreadable = nodes.some(
      (node) => node.data.variant === 'send_result' && _notNil(root) && node.data.parentApiId === root.data.apiId
    );
    return nodes.map((node) =>
      _isNil(node.data.parentApiId) ? sendRootStep(node, unreadable ? 'unreadable' : 'detecting', status) : node
    );
  }
  const confirmed = isConfirmed(status);
  return nodes.reduce<Array<AgentGraphNode>>((stepped, node) => {
    if (_isNil(node.data.parentApiId)) {
      stepped.push(sendRootStep(node, 'detected', status));
    } else if (node.id === confirm.id && node.data.variant === 'send_domain_confirm') {
      const step: GraphNodeStep = confirmed ? { state: 'done', label: 'Confirmed' } : { state: 'pending' };
      stepped.push({ ...node, data: { ...node.data, step } });
      if (confirmed) {
        stepped.push(writingReportStep(confirm, writingReportStepOf(nodes, confirm, status)));
      }
    } else if (confirmed && isConfirmedRunResult(node, confirm)) {
      stepped.push(underWritingReport(node, confirm));
    } else {
      stepped.push(node);
    }
    return stepped;
  }, []);
};
